/* ===== Backend Service - Cloud Functions Wrappers ===== */
import { showSnackbar } from '../utils/ui.js?v=82.86';
import { logger } from '../utils/logger.js?v=82.86';
import { queueOperation } from './offline-queue.js?v=82.86';
import { db, app as firebaseApp } from '../config/firebase.js?v=82.86';

// Use Firebase SDK v8 (compatible with script tags)
let functions = null;
if (typeof firebase !== 'undefined' && firebase.functions) {
  functions = firebaseApp && typeof firebaseApp.functions === 'function' ? firebaseApp.functions() : firebase.functions();
}

/**
 * Helper: Check if actually offline by attempting a lightweight fetch (quick check)
 */
async function isActuallyOffline() {
  if (!navigator.onLine) return true;
  
  try {
    // Quick check with shorter timeout (1 second) to avoid blocking
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 1000);
    
    await fetch('/favicon.ico', { 
      method: 'HEAD', 
      cache: 'no-cache',
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    return false; // Online
  } catch {
    return true; // Network error - assume offline
  }
}

/**
 * Submit Claim — online: scrie + validează CF înainte de success.
 * Offline: doar dacă campania e activă (settings cache), pending validation.
 */
export async function submitClaimBackend(claimData) {
  const { getCurrentUser } = await import('./auth.js?v=82.86');
  const { getCurrentSettings } = await import('./settings.js?v=82.86');
  
  const currentUser = getCurrentUser();
  if (!currentUser) {
    throw new Error('User not authenticated');
  }

  const settings = getCurrentSettings() || {};
  const activeCampaignId = String(settings.activeCampaignId || '').trim() || null;
  if (settings.campaignStatus !== 'active' || !activeCampaignId) {
    throw new Error('Campania nu este activă');
  }
  
  const isOffline = !navigator.onLine;
  
  const claimRef = db.collection('claims').doc();
  const claimId = claimRef.id;
  
  const dateValue = isOffline 
    ? firebase.firestore.Timestamp.now()
    : firebase.firestore.FieldValue.serverTimestamp();
  
  const claimDoc = {
    adminId: currentUser.uid,
    campaignId: activeCampaignId,
    name: claimData.name,
    contact: claimData.contact,
    prize: claimData.prize || 'Ne pare rău, nu ai câștigat!',
    gdprAccepted: true,
    marketingAccepted: !!claimData.marketingAccepted,
    responsibleId: claimData.responsibleId || null,
    responsibleName: claimData.responsibleName || null,
    receiptNumber: claimData.receiptNumber != null ? claimData.receiptNumber : null,
    versions: { privacy: '1.0.0', marketing: '1.0.0' },
    operator: {
      companyName: claimData.operator?.companyName || null,
      cui: claimData.operator?.cui || null,
      email: claimData.operator?.email || null
    },
    date: dateValue,
    _offlinePendingValidation: true
  };

  if (isOffline) {
    claimRef.set(claimDoc).catch((error) => {
      logger.error('[Backend] Firestore write failed (offline):', error);
      queueOperation({
        functionName: 'submitClaimBackend',
        args: [claimData]
      }).catch((err) => logger.error('[Backend] Failed to queue operation:', err));
    });
    showSnackbar('Revendicare salvată offline - va apărea în dashboard imediat');
    return { success: true, claimId, message: 'Revendicare salvată' };
  }

  try {
    await claimRef.set(claimDoc);
    logger.info('[Backend] Claim saved to Firestore:', claimId);
    await validateClaimViaCloudFunction(claimId, claimData, { silent: true });
    showSnackbar('Revendicare salvată');
    return { success: true, claimId, message: 'Revendicare salvată' };
  } catch (error) {
    await deleteClaimDocIfExists(claimRef, claimId);
    const msg = mapClaimValidationUserMessage(error);
    showSnackbar(msg, 'error');
    throw new Error(msg);
  }
}

function getCallableErrorCode(error) {
  const raw = String(error?.code || '');
  const parts = raw.split('/');
  return (parts[parts.length - 1] || '').toLowerCase();
}

function isPermanentClaimValidationError(error) {
  const code = getCallableErrorCode(error);
  return ['already-exists', 'failed-precondition', 'invalid-argument', 'permission-denied'].includes(code);
}

function mapClaimValidationUserMessage(error) {
  const code = getCallableErrorCode(error);
  if (code === 'already-exists') return 'Acest contact a revendicat deja.';
  if (code === 'failed-precondition') {
    const m = String(error?.message || '');
    if (/activă|activa|active/i.test(m)) return 'Campania nu este activă';
    return m.replace(/^[^:]+:\s*/, '').trim() || 'Revendicarea nu a putut fi validată.';
  }
  if (code === 'resource-exhausted') return 'Prea multe cereri. Încearcă din nou în câteva secunde.';
  return String(error?.message || 'Revendicarea nu a putut fi salvată.').replace(/^[^:]+:\s*/, '').trim();
}

async function deleteClaimDocIfExists(claimRef, claimId) {
  try {
    await claimRef.delete();
    logger.info('[Backend] Deleted invalid/pending claim:', claimId);
  } catch (deleteError) {
    logger.warn('[Backend] Failed to delete claim after validation error:', claimId, deleteError?.message);
  }
}

/**
 * ✅ Funcție separată pentru validare în background (non-blocking)
 * Această funcție rulează în background și nu blochează UI-ul
 */
async function validateClaimViaCloudFunction(claimId, claimData, options = {}) {
  const silent = !!options.silent;
  if (!functions) {
    logger.warn('[Backend] Functions not available, skipping validation');
    return;
  }
  
  try {
    logger.info('[Backend] Starting background validation for claim:', claimId);
    const submitClaim = functions.httpsCallable('submitClaim');
    
    // ✅ FIX: Include claimId pentru validare claim existent
    const submitPromise = submitClaim({
      claimId: claimId, // ✅ Required: Send claimId for validation
      name: claimData.name,
      contact: claimData.contact,
      prize: claimData.prize,
      gdprAccepted: claimData.gdprAccepted,
      marketingAccepted: claimData.marketingAccepted,
      responsibleId: claimData.responsibleId || null,
      responsibleName: claimData.responsibleName || null,
      receiptNumber: claimData.receiptNumber != null ? claimData.receiptNumber : null,
      operator: claimData.operator,
      consent: claimData.consent || null
    });
    
    const timeoutPromise = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Cloud Function validation timeout')), 30000)
    );
    
    const result = await Promise.race([submitPromise, timeoutPromise]);
    
    logger.info('[Backend] Claim validated via Cloud Function:', claimId, result.data);
    
    // ✅ FIX: Nu mai e nevoie de update manual - CF face update cu FieldValue.delete()
    
    return result.data;
  } catch (error) {
    logger.warn('[Backend] Claim validation failed:', claimId, {
      error: error.message,
      code: error.code
    });

    if (isPermanentClaimValidationError(error)) {
      try {
        await db.collection('claims').doc(claimId).delete();
        logger.info('[Backend] Deleted claim after permanent validation error:', claimId);
      } catch (deleteError) {
        logger.warn('[Backend] Could not delete claim after validation error:', claimId, deleteError?.message);
      }
    }
    
    const clientPage = document.getElementById('clientPage');
    const isClientMode = clientPage && clientPage.classList.contains('visible');
    
    if (!isClientMode && !silent) {
      if (getCallableErrorCode(error) === 'resource-exhausted') {
        showSnackbar('⚠️ Prea multe cereri - validare amânată (se va reîncerca automat)', 'warning');
      } else if (error.message && error.message.includes('timeout')) {
        showSnackbar('⚠️ Validare lentă - se reîncearcă în fundal', 'warning');
      } else if (getCallableErrorCode(error) === 'already-exists') {
        showSnackbar('⚠️ Contact duplicat - revendicarea a fost respinsă', 'error');
      } else if (getCallableErrorCode(error) === 'failed-precondition') {
        showSnackbar(mapClaimValidationUserMessage(error), 'error');
      } else {
        showSnackbar('Actualizare date în curs - se finalizează automat', 'info');
      }
    }
    
    throw error;
  }
}


/**
 * Initialize Settings via Cloud Function
 */
export async function initSettingsBackend(data) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const initSettings = functions.httpsCallable('initSettings');
    const result = await initSettings({
      companyName: data.companyName,
      cui: data.cui,
      opConsents: data.opConsents || {},
      vendorMarketing: data.vendorMarketing || {},
      locationName: data.locationName || '',
      locationCity: data.locationCity || ''
    });
    
    logger.info('[Backend] Settings initialized:', result.data);
    return result.data;
  } catch (error) {
    logger.error('[Backend] Init settings error:', error);
    const errorMessage = error.message || 'Eroare la inițializarea setărilor';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Save Campaign Settings via Cloud Function (with offline support)
 */
export async function saveCampaignSettingsBackend(settings) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  
  // If offline, queue the operation
  if (!navigator.onLine) {
    await queueOperation({
      functionName: 'saveCampaignSettingsBackend',
      args: [settings]
    });
    showSnackbar('Offline - setările vor fi salvate când revine conexiunea');
    return { success: true, queued: true };
  }
  
  try {
    const { withTimeoutMs } = await import('../utils/with-timeout.js?v=82.86');
    console.log('FIRESTORE SAVE START');
    const saveCampaignSettings = functions.httpsCallable('saveCampaignSettings');
    const result = await withTimeoutMs(saveCampaignSettings(settings), 60000);
    console.log('FIRESTORE SAVE DONE');
    
    logger.info('[Backend] Settings saved:', result.data);
    return result.data;
  } catch (error) {
    if (error?.message === 'timeout') {
      const timeoutErr = new Error('Salvarea pe server a durat prea mult. Încearcă din nou.');
      showSnackbar(timeoutErr.message, 'error');
      throw timeoutErr;
    }
    // If error is network-related, queue the operation
    if (!navigator.onLine || error.code === 'unavailable' || error.message.includes('network') || error.message.includes('Failed to fetch')) {
      await queueOperation({
        functionName: 'saveCampaignSettingsBackend',
        args: [settings]
      });
      showSnackbar('Conexiune întreruptă - setările vor fi salvate când revine conexiunea');
      return { success: true, queued: true };
    }
    
    logger.error('[Backend] Save settings error:', error);
    const errorMessage = error.message || 'Eroare la salvarea setărilor';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Rebuild publicSites/{slug} from current settings (for /m/{slug} visitors).
 */
export async function refreshPublicSiteBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  if (!navigator.onLine) {
    return { success: false, skipped: true, reason: 'offline' };
  }
  try {
    const fn = functions.httpsCallable('refreshPublicSite');
    const result = await fn({});
    logger.info('[Backend] Public site refreshed:', result.data);
    return result.data;
  } catch (error) {
    logger.warn('[Backend] refreshPublicSite error:', error);
    throw error;
  }
}

export async function suggestClientLayoutAIBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }

  try {
    const fn = functions.httpsCallable('suggestClientLayoutAI');
    const result = await fn({
      businessType: payload?.businessType || '',
      goal: payload?.goal || '',
      preferredTone: payload?.preferredTone || '',
      currentLayoutConfig: payload?.currentLayoutConfig || {},
      allowCreativeReorder: payload?.allowCreativeReorder === true,
      templatePick: payload?.templatePick === true
    });
    return result.data || {};
  } catch (error) {
    logger.error('[Backend] suggestClientLayoutAI error:', error);
    if (payload?.suppressErrorSnackbar !== true) {
      const errorMessage = error?.message || 'Nu am putut genera propunerea AI';
      showSnackbar(errorMessage, 'error');
    }
    throw error;
  }
}

/**
 * Start Campaign via Cloud Function
 */
export async function startCampaignBackend(notary = null) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const startCampaign = functions.httpsCallable('startCampaign');
    const result = await startCampaign({ notary: notary || null });
    
    logger.info('[Backend] Campaign started:', result.data);
    // Mesajul de succes este afișat de confirmStartCampaign
    return result.data;
  } catch (error) {
    logger.error('[Backend] Start campaign error:', error);
    const errorMessage = error.message || 'Eroare la pornirea campaniei';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Stop Campaign via Cloud Function
 */
export async function stopCampaignBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const stopCampaign = functions.httpsCallable('stopCampaign');
    const result = await stopCampaign();
    
    logger.info('[Backend] Campaign stopped:', result.data);
    // Mesajul de succes este afișat de confirmStopCampaign
    return result.data;
  } catch (error) {
    logger.error('[Backend] Stop campaign error:', error);
    const errorMessage = error.message || 'Eroare la oprirea campaniei';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Reset Tries via Cloud Function
 */
export async function resetTriesBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const resetTries = functions.httpsCallable('resetTries');
    const result = await resetTries();
    
    logger.info('[Backend] Tries reset:', result.data);
    showSnackbar(result.data.message);
    return result.data;
  } catch (error) {
    logger.error('[Backend] Reset tries error:', error);
    const errorMessage = error.message || 'Eroare la resetarea încercărilor';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Delete All Claims via Cloud Function
 */
export async function deleteAllClaimsBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const deleteAllClaims = functions.httpsCallable('deleteAllClaims');
    const result = await deleteAllClaims();
    
    logger.info('[Backend] Claims deleted:', result.data);
    showSnackbar(result.data.message);
    return result.data;
  } catch (error) {
    logger.error('[Backend] Delete claims error:', error);
    const errorMessage = error.message || 'Eroare la ștergerea revendicărilor';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Export User Data via Cloud Function
 */
export async function exportUserDataBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const exportUserData = functions.httpsCallable('exportUserData');
    const result = await exportUserData();
    
    logger.info('[Backend] Data exported:', result.data);
    
    const dataStr = JSON.stringify(result.data.data, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `spressjoy-data-${new Date().toISOString()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    showSnackbar('Date exportate cu succes!');
    return result.data;
  } catch (error) {
    logger.error('[Backend] Export data error:', error);
    const errorMessage = error.message || 'Eroare la exportul datelor';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Delete User Account via Cloud Function
 */
export async function deleteUserAccountBackend(confirmText) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const deleteUserAccount = functions.httpsCallable('deleteUserAccount');
    const result = await deleteUserAccount({ confirmText });
    
    logger.info('[Backend] Account deleted:', result.data);
    showSnackbar(result.data.message);
    return result.data;
  } catch (error) {
    logger.error('[Backend] Delete account error:', error);
    const errorMessage = error.message || 'Eroare la ștergerea contului';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Delete Archived Campaign via Cloud Function
 */
export async function deleteArchivedCampaignBackend(runId) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const deleteArchivedCampaign = functions.httpsCallable('deleteArchivedCampaign');
    const result = await deleteArchivedCampaign({ runId });
    
    logger.info('[Backend] Archived campaign deleted:', result.data);
    showSnackbar(result.data.message);
    return result.data;
  } catch (error) {
    logger.error('[Backend] Delete archived campaign error:', error);
    const errorMessage = error.message || 'Eroare la ștergerea campaniei';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Draw Winner via Cloud Function
 */
export async function drawWinnerBackend(runId) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const drawWinner = functions.httpsCallable('drawWinner');
    const result = await drawWinner({ runId });
    
    logger.info('[Backend] Winner drawn:', result.data);
    showSnackbar(result.data.message);
    return result.data;
  } catch (error) {
    logger.error('[Backend] Draw winner error:', error);
    const errorMessage = error.message || 'Eroare la extragerea câștigătorului';
    showSnackbar(errorMessage);
    throw error;
  }
}

export async function drawWinnersTop3Backend(runId) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  try {
    const drawWinnersTop3 = functions.httpsCallable('drawWinnersTop3');
    const result = await drawWinnersTop3({ runId });
    
    logger.info('[Backend] Top 3 winners drawn:', result.data);
    showSnackbar(result.data.message);
    return result.data;
  } catch (error) {
    logger.error('[Backend] Draw top 3 winners error:', error);
    const errorMessage = error.message || 'Eroare la extragerea câștigătorilor';
    showSnackbar(errorMessage);
    throw error;
  }
}

/**
 * Update Game Stats via Cloud Function
 * ✅ FIX: Event-based (delta only) - primește { playId, decrementRemainingTries, prizeId }
 * ✅ FIX: Queue offline pentru prizes + remainingTries sync
 * ✅ FIX: Protecție anti-re-queue când rulează din queue (flag fromQueue)
 */
export async function updateGameStatsBackend(
  eventUpdate,
  { fromQueue = false, publicSlug = null } = {}
) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }

  const queueExtra = publicSlug ? { publicSlug } : {};
  
  // ✅ FIX: Dacă ești offline și NU ești apelat din queue, queue-uiește
  if (!navigator.onLine && !fromQueue) {
    logger.info('[Backend] Offline - queueing updateGameStats for sync when online');
    try {
      await queueOperation({
        functionName: 'updateGameStatsBackend',
        args: Object.keys(queueExtra).length ? [eventUpdate, queueExtra] : [eventUpdate]
      });
      return { success: true, queued: true };
    } catch (queueError) {
      logger.error('[Backend] Failed to queue updateGameStats:', queueError);
      return { success: false, queued: false, error: queueError.message };
    }
  }
  
  try {
    const updateGameStats = functions.httpsCallable('updateGameStats');
    const payload = { ...eventUpdate };
    if (publicSlug) {
      payload.publicSlug = publicSlug;
    }
    const result = await updateGameStats(payload);
    return { success: true, ...result.data }; // ✅ Returnează success util
  } catch (error) {
    logger.error('[Backend] Update game stats error:', error);
    
    // ✅ FIX: Dacă eșuează din cauza lipsei de conexiune și NU ești din queue, queue-uiește
    const isNetworkError = !navigator.onLine || 
                           error.code === 'unavailable' || 
                           error.code === 'deadline-exceeded' ||
                           error.message?.includes('network') ||
                           error.message?.includes('fetch');
    
    if (isNetworkError && !fromQueue) {
      logger.info('[Backend] Network error - queueing updateGameStats for retry');
      try {
        await queueOperation({
          functionName: 'updateGameStatsBackend',
          args: Object.keys(queueExtra).length ? [eventUpdate, queueExtra] : [eventUpdate]
        });
        return { success: true, queued: true, retry: true };
      } catch (queueError) {
        logger.error('[Backend] Failed to queue updateGameStats after network error:', queueError);
        return { success: false, queued: false, error: queueError.message };
      }
    }
    
    // Dacă e din queue și tot eșuează, aruncă eroare (queue-ul va face retry)
    throw error;
  }
}

/**
 * Revendicare din modul public (QR) — fără cont; validare server după slug.
 */
/** Comerciantul validează codul de voucher arătat de client. */
export async function redeemVoucherBackend(code) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('redeemVoucher');
  const result = await fn({ code: String(code || '') });
  return result.data;
}

/** Răzuiește: primește biletul pentru un bon (același bon → același bilet). */
export async function scratchTicketBackend(publicSlug, photo = {}, consent = {}) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('scratchTicket', { timeout: 70000 });
  const result = await fn({
    publicSlug,
    imageBase64: photo.imageBase64 || '',
    mimeType: photo.mimeType || 'image/jpeg',
    deviceId: photo.deviceId || '',
    manual: photo.manual ? { number: String(photo.manual.number || '').slice(0, 12), date: String(photo.manual.date || '').slice(0, 10) } : null,
    gdprAccepted: consent.gdpr === true,
    marketingAccepted: consent.mkt === true,
    contact: consent.mkt === true ? String(consent.contact || '').slice(0, 120) : ''
  });
  return result.data;
}

/** Răzuiește: lista biletelor + cheia de verificare (doar după încheiere). */
export async function exportScratchDeckBackend(runId) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const r = await functions.httpsCallable('exportScratchDeck')({ runId: String(runId || '') });
  return r.data;
}

/** Campanie arhivată + înscrieri, prin HTTPS (rezervă când citirea Firestore din browser nu răspunde). */
export async function getArchivedRunBackend(runId) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const r = await functions.httpsCallable('getArchivedRun')({ runId: String(runId || '') });
  return r.data;
}

/** Tombola: extragerea câștigătorilor pe locuri + rezerve. */
export async function drawTombolaBackend(runId) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const r = await functions.httpsCallable('drawTombola')({ runId: String(runId || '') });
  return r.data;
}
export async function updateTombolaWinnerBackend(runId, place, action, reason = '') {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const r = await functions.httpsCallable('updateTombolaWinner')({ runId: String(runId || ''), place, action, reason });
  return r.data;
}
export async function publishTombolaWinnersBackend(runId) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const r = await functions.httpsCallable('publishTombolaWinners')({ runId: String(runId || '') });
  return r.data;
}

/** Retragerea consimțământului de marketing al unui participant (înregistrată în istoric). */
export async function withdrawMarketingConsentBackend(claimId, channel = 'all') {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('withdrawMarketingConsent');
  const result = await fn({ claimId: String(claimId || ''), channel });
  return result.data;
}

/** Clientul își folosește voucherul de pe pagina publică. */
export async function redeemPublicVoucherBackend(publicSlug, code, onlyCheck = false) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('redeemPublicVoucher');
  const result = await fn({ publicSlug, code: String(code || ''), onlyCheck });
  return result.data;
}

export async function submitPublicClaimBackend(publicSlug, claimPayload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  if (!publicSlug) {
    throw new Error('Slug lipsă');
  }
  const fn = functions.httpsCallable('submitPublicClaim');
  const result = await fn({
    publicSlug,
    name: claimPayload.name,
    contact: claimPayload.contact,
    prize: claimPayload.prize,
    gdprAccepted: claimPayload.gdprAccepted,
    marketingAccepted: claimPayload.marketingAccepted,
    responsibleId: claimPayload.responsibleId || null,
    responsibleName: claimPayload.responsibleName || null,
    receiptNumber: claimPayload.receiptNumber != null ? claimPayload.receiptNumber : null,
    ticketId: claimPayload.ticketId || null,
    claimToken: claimPayload.claimToken || null,
    operator: claimPayload.operator || {},
    consent: claimPayload.consent || null
  });
  return result.data;
}

/**
 * Community lead submit (client/public)
 */
export async function submitCommunityLeadBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('submitCommunityLead');
  const result = await fn({
    publicSlug: payload?.publicSlug || null,
    contact: payload?.contact || '',
    contactType: payload?.contactType || '',
    marketingAccepted: payload?.marketingAccepted === true,
    source: payload?.source || 'client',
    companyName: payload?.companyName || null
  });
  return result.data;
}

export async function submitEventMessageBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('submitEventMessage');
  const result = await fn({
    publicSlug: payload?.publicSlug || null,
    text: payload?.text || '',
    source: payload?.source || 'client'
  });
  return result.data;
}

export async function markEventMessageReadBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('markEventMessageRead');
  const result = await fn({ messageId: payload?.messageId || '' });
  return result.data;
}

export async function deleteEventMessageBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('deleteEventMessage');
  const result = await fn({ messageId: payload?.messageId || '' });
  return result.data;
}

export async function deleteAllEventMessagesBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('deleteAllEventMessages');
  const result = await fn({});
  return result.data;
}

export async function trackClientInterfaceEventBackend(payload) {
  if (!functions) {
    return { success: false };
  }
  const fn = functions.httpsCallable('trackClientInterfaceEvent');
  const result = await fn({
    publicSlug: payload?.publicSlug || null,
    moduleKey: payload?.moduleKey || '',
    moduleLabel: payload?.moduleLabel || '',
    actionType: payload?.actionType || 'click',
    source: payload?.source || null,
    sessionId: payload?.sessionId || null,
    deviceType: payload?.deviceType || null,
    pageId: payload?.pageId || null
  });
  return result.data;
}

export async function getClientInterfaceAnalyticsBackend(period = '7d', extra = {}) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('getClientInterfaceAnalytics', { timeout: 30000 });
  const result = await fn({ period, ...(extra || {}) });
  return result.data;
}

export async function resetClientInterfaceAnalyticsBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('resetClientInterfaceAnalytics');
  const result = await fn({});
  return result.data;
}

export async function getLoyaltySettingsBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('getLoyaltySettings');
  const result = await fn({});
  return result.data;
}

export async function saveLoyaltySettingsBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('saveLoyaltySettings');
  const result = await fn(payload || {});
  return result.data;
}

export async function registerLoyaltyPurchaseBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('registerLoyaltyPurchase');
  const result = await fn({
    email: payload?.email || '',
    receiptNumber: payload?.receiptNumber || '',
    publicSlug: payload?.publicSlug || null,
    source: payload?.source || 'public'
  });
  return result.data;
}

export async function checkLoyaltyProgressBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('checkLoyaltyProgress');
  const result = await fn({
    email: payload?.email || '',
    phone: payload?.phone || '',
    publicSlug: payload?.publicSlug || null
  });
  return result.data;
}

export async function listLoyaltyMembersBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('listLoyaltyMembers');
  const result = await fn({});
  return result.data;
}

export async function awardLoyaltyRewardBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('awardLoyaltyReward');
  const result = await fn({ memberId: payload?.memberId || '' });
  return result.data;
}

export async function deleteLoyaltyMemberBackend(payload) {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('deleteLoyaltyMember');
  const result = await fn({ memberId: payload?.memberId || '' });
  return result.data;
}

export async function deleteAllLoyaltyDataBackend() {
  if (!functions) {
    throw new Error('Firebase Functions not initialized');
  }
  const fn = functions.httpsCallable('deleteAllLoyaltyData');
  const result = await fn({});
  return result.data;
}

/**
 * Șterge claims pending când campania nu e activă (orfane din path-ul vechi).
 */
export async function purgeInvalidPendingClaims() {
  const { getCurrentUser } = await import('./auth.js?v=82.86');
  const { getCurrentSettings } = await import('./settings.js?v=82.86');
  const currentUser = getCurrentUser();
  if (!currentUser || !navigator.onLine) return { deleted: 0 };

  const settings = getCurrentSettings() || {};
  const campaignActive =
    settings.campaignStatus === 'active' && String(settings.activeCampaignId || '').trim();
  if (campaignActive) return { deleted: 0 };

  try {
    const snap = await db
      .collection('claims')
      .where('adminId', '==', currentUser.uid)
      .where('_offlinePendingValidation', '==', true)
      .limit(25)
      .get();
    if (snap.empty) return { deleted: 0 };

    let deleted = 0;
    for (const doc of snap.docs) {
      try {
        await doc.ref.delete();
        deleted += 1;
      } catch (_) {
        /* ignore */
      }
    }
    if (deleted) logger.info('[Backend] Purged pending claims (campaign inactive):', deleted);
    return { deleted };
  } catch (error) {
    logger.warn('[Backend] purgeInvalidPendingClaims failed:', error?.message);
    return { deleted: 0, error: error?.message };
  }
}

/**
 * Retry automat pentru claims nevalidate.
 */
export async function retryPendingClaimValidations() {
  if (!navigator.onLine) {
    logger.info('[Backend] Offline - skipping pending claim validations');
    return { success: false, reason: 'offline' };
  }
  
  const { getCurrentUser } = await import('./auth.js?v=82.86');
  const { getCurrentSettings } = await import('./settings.js?v=82.86');
  const currentUser = getCurrentUser();
  
  if (!currentUser) {
    logger.info('[Backend] No user authenticated - skipping pending claim validations');
    return { success: false, reason: 'not-authenticated' };
  }

  const settings = getCurrentSettings() || {};
  const campaignActive =
    settings.campaignStatus === 'active' && String(settings.activeCampaignId || '').trim();
  if (!campaignActive) {
    const purged = await purgeInvalidPendingClaims();
    return { success: true, validatedCount: 0, purged: purged.deleted || 0 };
  }
  
  try {
    const claimsSnapshot = await db.collection('claims')
      .where('adminId', '==', currentUser.uid)
      .where('_offlinePendingValidation', '==', true)
      .limit(10)
      .get();
    
    if (claimsSnapshot.empty) {
      logger.info('[Backend] No pending claims to validate');
      return { success: true, validatedCount: 0 };
    }
    
    logger.info(`[Backend] Found ${claimsSnapshot.size} pending claims - attempting validation`);
    
    let successCount = 0;
    let failCount = 0;
    
    for (const doc of claimsSnapshot.docs) {
      try {
        const claimData = doc.data();
        const validationData = {
          name: claimData.name,
          contact: claimData.contact,
          prize: claimData.prize,
          gdprAccepted: claimData.gdprAccepted,
          marketingAccepted: claimData.marketingAccepted,
          responsibleId: claimData.responsibleId || null,
          responsibleName: claimData.responsibleName || null,
          receiptNumber: claimData.receiptNumber != null ? claimData.receiptNumber : null,
          operator: claimData.operator
        };
        
        await validateClaimViaCloudFunction(doc.id, validationData, { silent: true });
        successCount++;
        logger.info('[Backend] Successfully validated pending claim:', doc.id);
        
      } catch (error) {
        failCount++;
        logger.warn('[Backend] Failed to validate pending claim:', doc.id, error.message);
        
        if (isPermanentClaimValidationError(error)) {
          try {
            await doc.ref.delete();
            logger.info('[Backend] Deleted invalid pending claim:', doc.id);
          } catch (deleteError) {
            logger.warn('[Backend] Failed to delete invalid claim:', doc.id, deleteError.message);
          }
        }
      }
    }
    
    logger.info(`[Backend] Pending claim validation completed - success: ${successCount}, failed: ${failCount}`);
    return { success: true, validatedCount: successCount, failedCount: failCount };
    
  } catch (error) {
    logger.error('[Backend] Error during pending claim validation:', error);
    return { success: false, error: error.message };
  }
}

export async function ensureSuperadminClaimBackend() {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('ensureSuperadminClaim');
  const result = await fn({});
  return result.data;
}

export async function checkMerchantAccessBackend() {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('checkMerchantAccess');
  const result = await fn({});
  return result.data;
}

export async function superadminListMerchantsBackend(payload = {}) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminListMerchants');
  const result = await fn({
    search: payload.search || '',
    statusFilter: payload.statusFilter || 'all'
  });
  return result.data;
}

export async function superadminGetMerchantBackend(uid) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminGetMerchant');
  const result = await fn({ uid });
  return result.data;
}

export async function superadminBlockMerchantBackend(uid, reason) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminBlockMerchant');
  const result = await fn({ uid, reason: reason || '' });
  return result.data;
}

export async function superadminUnblockMerchantBackend(uid, planDurationMonths) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminUnblockMerchant');
  const result = await fn({ uid, planDurationMonths });
  return result.data;
}

export async function superadminSetSubscriptionBackend(uid, durationMonths) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminSetSubscription');
  const result = await fn({ uid, durationMonths });
  return result.data;
}

export async function superadminDeleteMerchantBackend(uid, confirmText) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminDeleteMerchant');
  const result = await fn({ uid, confirmText });
  return result.data;
}

/* ===== Demo-uri interne (prospectare) ===== */
export async function superadminCreateDemoSiteBackend(payload = {}) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  // Joy caută locația înainte de creare → poate dura până la ~2 minute
  const fn = functions.httpsCallable('superadminCreateDemoSite', { timeout: 160000 });
  const result = await fn(payload);
  return result.data;
}

export async function superadminExtendDemoBackend(uid, days = 7) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminExtendDemo');
  const result = await fn({ uid, days });
  return result.data;
}

/** Link de vizită pentru prospect (intră în panoul demo-ului fără cont, mod vizitator). */
export async function superadminCreateDemoVisitLinkBackend(uid, days = 7) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminCreateDemoVisitLink');
  const result = await fn({ uid, days });
  return result.data;
}

/** Prospectul deschide /?vizita=TOKEN → token de sesiune (vizitator). */
export async function demoVisitLoginBackend(token) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('demoVisitLogin');
  const result = await fn({ token });
  return result.data;
}

export async function superadminExtendSubscriptionBackend(uid, days = 30) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminExtendSubscription');
  const result = await fn({ uid, days });
  return result.data;
}

export async function superadminActivateDemoBackend(uid, email, months = 1) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminActivateDemo');
  const result = await fn({ uid, email, months });
  return result.data;
}

export async function superadminOpenDemoSiteBackend(uid) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminOpenDemoSite');
  const result = await fn({ uid });
  return result.data;
}

export async function superadminOpenSupportEditBackend(uid) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminOpenSupportEdit');
  const result = await fn({ uid });
  return result.data;
}

export async function demoReturnToSuperadminBackend() {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('demoReturnToSuperadmin');
  const result = await fn({});
  return result.data;
}

/* ===== Configurare rapidă cu AI ===== */
export async function quickSetupResearchBackend(payload) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('quickSetupResearch', { timeout: 130000 });
  const result = await fn({
    name: payload?.name || '',
    city: payload?.city || '',
    facebookUrl: payload?.facebookUrl || '',
    mapsUrl: payload?.mapsUrl || '',
    notes: payload?.notes || ''
  });
  return result.data || {};
}

export async function generateAiPosterBackend(payload) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('generateAiPoster', { timeout: 130000 });
  const result = await fn({
    kind: payload?.kind || 'campaign',
    businessName: payload?.businessName || '',
    businessType: payload?.businessType || '',
    title: payload?.title || '',
    lines: Array.isArray(payload?.lines) ? payload.lines : [],
    mode: payload?.mode === 'custom' ? 'custom' : 'auto',
    customPrompt: payload?.customPrompt || '',
    referenceImageUrl: payload?.referenceImageUrl || ''
  });
  return result.data || {};
}

export async function getAiPosterQuotaBackend() {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('getAiPosterQuota');
  const result = await fn({});
  return result.data || {};
}

export async function scanLoyaltyReceiptBackend(payload) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('scanLoyaltyReceipt', { timeout: 70000 });
  const result = await fn({
    phone: payload?.phone || '',
    consent: payload?.consent === true,
    imageBase64: payload?.imageBase64 || '',
    mimeType: payload?.mimeType || 'image/jpeg',
    publicSlug: payload?.publicSlug || null,
    marketingSms: payload?.marketingSms === true
  });
  return result.data || {};
}

/** CUI → numele firmei și localitatea (ANAF). Merge și fără cont (la „Cont nou”). */
export async function lookupCompanyByCuiBackend(cui) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('lookupCompanyByCui', { timeout: 20000 });
  const result = await fn({ cui });
  return result.data;
}

/** Creează automat adresa publică (/m/slug) dacă lipsește. Întoarce { slug, created }. */
export async function ensurePublicSlugBackend() {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('ensurePublicSlug', { timeout: 20000 });
  const result = await fn({});
  return result.data;
}

/** Clasamentul fidelizării (modul „Clasament”). */
export async function getLoyaltyRankingBackend(payload = {}) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('getLoyaltyRanking');
  const result = await fn({ publicSlug: payload.publicSlug || null });
  return result.data;
}

/** Super Admin: planul și funcțiile unui comerciant. */
export async function superadminSetPlanBackend(uid, planTier, featureOverrides) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('superadminSetPlan');
  const result = await fn({ uid, planTier: planTier || '', featureOverrides: featureOverrides || {} });
  return result.data;
}

/** Lista de marketing (toți cei cu acord de oferte, din toate sursele, fără dubluri). */
export async function listMarketingAudienceBackend() {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const fn = functions.httpsCallable('listMarketingAudience', { timeout: 30000 });
  const result = await fn({});
  return result.data || {};
}

/** Regulament nou / act adițional în timpul campaniei (organizator = firma localului). */
export async function updateOwnRulesBackend(payload) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  const res = await functions.httpsCallable('updateOwnRules')(payload);
  return res.data;
}

/** Superadmin: Joy pe voce pentru un comerciant (pornit/oprit + conversații pe lună). */
export async function superadminGetJoyVoiceBackend(uid) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  return (await functions.httpsCallable('superadminGetJoyVoice')({ uid })).data;
}
export async function superadminSetJoyVoiceBackend(uid, enabled, monthlyLimit) {
  if (!functions) throw new Error('Firebase Functions not initialized');
  return (await functions.httpsCallable('superadminSetJoyVoice')({ uid, enabled, monthlyLimit })).data;
}
